import { For, Show, createResource, createSignal } from 'solid-js';
import { useParams, useNavigate } from '@solidjs/router';
import { useApi, isOk } from '../api';
import type { SelfShutdownIncidentReportDto } from '../api';
import Text from '../components/Text';
import MinecraftText from '../components/MinecraftText';
import { useTranslation } from '../hooks/useTranslation';
import { useToast } from '../components/MessageBox';
import { EmptyState } from '../components/EmptyState';
import { SkeletonAuditRow } from '../components/Skeleton';
import { formatDateTime } from '../services/dateFormat';
import { notifySelfShutoffAcknowledged, selfShutoffTriggerKey } from '../services/selfShutoff';
import styles from './SelfShutoffReport.module.css';

export default function SelfShutoffReport() {
    const params = useParams();
    const api = useApi();
    const navigate = useNavigate();
    const toast = useToast();
    const { t } = useTranslation();

    const [reloadToken, setReloadToken] = createSignal(0);
    const [acknowledging, setAcknowledging] = createSignal(false);

    const [report] = createResource(
        () => ({ id: params.id, token: reloadToken() }),
        async (p): Promise<SelfShutdownIncidentReportDto | null> => {
            if (!p.id) return null;
            try {
                const res = await api.botSelfShutdown.getReport(p.id);
                if (isOk(res.data)) return res.data.value;
            } catch { /* fall through to the toast */ }
            toast?.error(t('SELF_SHUTOFF_REPORT_FETCH_FAILED'));
            return null;
        }
    );

    const incident = () => report()?.incident;
    const loading = () => report.state === 'pending';

    const acknowledge = async () => {
        const id = params.id;
        if (!id) return;
        setAcknowledging(true);
        try {
            const res = await api.botSelfShutdown.acknowledge(id);
            if (isOk(res.data)) {
                notifySelfShutoffAcknowledged();
                setReloadToken((n) => n + 1);
            } else {
                toast?.error(t('SELF_SHUTOFF_ACK_FAILED'));
            }
        } catch {
            toast?.error(t('SELF_SHUTOFF_ACK_FAILED'));
        } finally {
            setAcknowledging(false);
        }
    };

    return (
        <div class={styles.container}>
            <div class={styles.headerRow}>
                <h1><Text val="SELF_SHUTOFF_REPORT_TITLE" /></h1>
                <div class="h" style={{ gap: 'var(--spacing-sm)' }}>
                    <Show when={incident()?.botRegistryId}>
                        <button
                            class="btn btn-secondary btn-sm"
                            onClick={() => navigate(`/bots/${incident()!.botRegistryId}/auditlog`)}
                        >
                            <Text val="SELF_SHUTOFF_OPEN_AUDIT_LOG" />
                        </button>
                    </Show>
                    <Show when={incident() && !incident()!.acknowledgedAtUtc}>
                        <button class="btn btn-primary btn-sm" disabled={acknowledging()} onClick={() => void acknowledge()}>
                            <Show when={acknowledging()} fallback={<Text val="SELF_SHUTOFF_ACKNOWLEDGE" />}>
                                <i class="fa-solid fa-spinner fa-spin" />
                            </Show>
                        </button>
                    </Show>
                </div>
            </div>

            <Show when={loading()}>
                <div class="fcard v" style={{ gap: 'var(--spacing-sm)' }}>
                    <SkeletonAuditRow />
                    <SkeletonAuditRow />
                    <SkeletonAuditRow />
                </div>
            </Show>

            <Show when={!loading() && !incident()}>
                <EmptyState icon="fa-gavel" title={t('SELF_SHUTOFF_REPORT_NOT_FOUND')} />
            </Show>

            <Show when={!loading() && incident()}>
                <div class="fcard">
                    <div class={styles.summary}>
                        <div class={styles.field}>
                            <span class={styles.label}><Text val="SELF_SHUTOFF_FIELD_BOT" /></span>
                            <span class={`${styles.value} ${styles.mono}`}>{incident()!.botUsername}</span>
                        </div>
                        <div class={styles.field}>
                            <span class={styles.label}><Text val="SELF_SHUTOFF_FIELD_REASON" /></span>
                            <span class={styles.value}>{t(selfShutoffTriggerKey(incident()!.trigger))}</span>
                        </div>
                        <div class={styles.field}>
                            <span class={styles.label}><Text val="SELF_SHUTOFF_FIELD_WHEN" /></span>
                            <span class={styles.value}>{formatDateTime(incident()!.occurredAtUtc)}</span>
                        </div>
                        <div class={styles.field}>
                            <span class={styles.label}><Text val="SELF_SHUTOFF_FIELD_RUNNING_TASK" /></span>
                            <span class={`${styles.value} ${styles.mono}`}>
                                {incident()!.lastRunningTaskKey ?? t('SELF_SHUTOFF_NO_RUNNING_TASK')}
                            </span>
                        </div>
                        <div class={styles.field}>
                            <span class={styles.label}><Text val="SELF_SHUTOFF_FIELD_AUTO_RESUME" /></span>
                            <span class={styles.value}>
                                {report()!.autoResumeCleared
                                    ? t('SELF_SHUTOFF_AUTO_RESUME_CLEARED')
                                    : t('SELF_SHUTOFF_AUTO_RESUME_STILL_SET')}
                            </span>
                        </div>
                        <div class={styles.field}>
                            <span class={styles.label}><Text val="SELF_SHUTOFF_FIELD_ACKNOWLEDGED" /></span>
                            <span class={`${styles.value} ${incident()!.acknowledgedAtUtc ? styles.acknowledged : ''}`}>
                                {incident()!.acknowledgedAtUtc
                                    ? t('SELF_SHUTOFF_ACKNOWLEDGED_BY',
                                        incident()!.acknowledgedByUsername ?? '?',
                                        formatDateTime(incident()!.acknowledgedAtUtc!))
                                    : t('SELF_SHUTOFF_NOT_ACKNOWLEDGED')}
                            </span>
                        </div>
                    </div>
                </div>

                <Show when={incident()!.detail}>
                    <div class="fcard v" style={{ gap: 'var(--spacing-md)' }}>
                        <h2 style={{ margin: 0 }}><Text val="SELF_SHUTOFF_SECTION_DETAIL" /></h2>
                        <pre class={styles.detailBlock}>{incident()!.detail}</pre>
                    </div>
                </Show>

                <div class="fcard v" style={{ gap: 'var(--spacing-md)' }}>
                    <h2 style={{ margin: 0 }}><Text val="SELF_SHUTOFF_SECTION_AUDIT" /></h2>
                    <Show
                        when={(report()!.audit ?? []).length > 0}
                        fallback={<EmptyState icon="fa-clipboard-list" title={t('SELF_SHUTOFF_NO_AUDIT')} />}
                    >
                        <div class={styles.logScroll}>
                            <For each={report()!.audit}>{(line) => (
                                <div class={styles.logRow}>
                                    <span class={styles.logTime}>{formatDateTime(line.createdAt)}</span>
                                    <span class={styles.logAction}>{line.actionType}</span>
                                    <span class={styles.logText}>{line.description}</span>
                                </div>
                            )}</For>
                        </div>
                    </Show>
                </div>

                <div class="fcard v" style={{ gap: 'var(--spacing-md)' }}>
                    <h2 style={{ margin: 0 }}><Text val="SELF_SHUTOFF_SECTION_CHAT" /></h2>
                    <Show
                        when={(report()!.chat ?? []).length > 0}
                        fallback={<EmptyState icon="fa-comments" title={t('SELF_SHUTOFF_NO_CHAT')} />}
                    >
                        <div class={styles.logScroll}>
                            <For each={report()!.chat}>{(line) => (
                                <div class={styles.logRow}>
                                    <span class={styles.logTime}>{formatDateTime(line.createdAt)}</span>
                                    <span class={styles.logText}><MinecraftText text={line.message ?? ''} /></span>
                                </div>
                            )}</For>
                        </div>
                    </Show>
                </div>
            </Show>
        </div>
    );
}
